import React, { useState, useEffect } from "react";
import { Box, Typography, Button, IconButton, Divider } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate, Link } from "react-router-dom";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import EditIcon from "@mui/icons-material/Edit";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";

// Sample product image - replace with actual image
import placeholderImage from "../assets/image-shipping.png";

export default function OrderDetails() {
  const { orderId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Sample order data - replace with actual data
  const [orderData] = useState({
    orderId: orderId || "ORD-123456",
    product: {
      name: "LEGO Classic Creative Bricks Set",
      condition: "Like New",
      image: placeholderImage,
      purchaseDate: "December 16, 2025",
      buyer: "@toycollector",
      toyUuid: "toy-uuid-12345", // Sample toy UUID for linking
    },
    pricing: {
      price: 8.45,
      platformFee: 0.5,
      total: 8.95,
    },
    shipping: {
      from: {
        name: "John Seller",
        address: "123 Main St",
        city: "Los Angeles, CA 90001",
        country: "United States",
      },
      to: {
        name: "Emily Buyer",
        address: "789 Oak Rd",
        city: "New York, NY 10022",
        country: "United States",
        userUuid: "buyer-uuid-12345", // Sample buyer UUID
      },
    },
  });

  // Steps data
  const steps = [
    {
      id: 1,
      title: "Order Confirmed",
      description: "Buyer has completed payment",
    },
    {
      id: 2,
      title: "Create Label",
      description: "Generate your UPS shipping label",
    },
    {
      id: 3,
      title: "Ship Item",
      description: "Pack and hand over to UPS",
    },
  ];

  const handleBackClick = () => {
    navigate("/seller/orders");
  };

  const handleCreateLabel = () => {
    // Navigate to create shipping label page
    navigate(`/seller/orders/${orderData.orderId}/shipper`);
  };

  return (
    <>
      <Helmet>
        <title>Order Details - {orderData.orderId} | R3PLAY</title>
        <meta
          name="description"
          content="Order details and shipping information"
        />
      </Helmet>

      <Box
        sx={{
          backgroundColor: "#F5F5F5",
          minHeight: "100vh",
          py: 4,
        }}
      >
        <Box
          sx={{
            maxWidth: "1440px",
            px: "100px",
            "@media (max-width: 1024px)": {
              px: "16px",
            },
          }}
        >
          <Box sx={{ boxShadow: "0px 4px 12px 0px rgba(0, 0, 0, 0.08)" }}>
            {/* Header */}
            <Box
              sx={{
                backgroundColor: "#FFFFFF",
                py: 2,
              }}
            >
              {/* Order Status */}
              <Box sx={{ px: 3 }}>
                <Typography
                  sx={{
                    fontSize: "24px",
                    fontWeight: 600,
                    color: "#000",
                  }}
                >
                  Order Confirmed
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#666",
                    mb: 2,
                  }}
                >
                  Buyer has completed payment
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {/* Progress Steps */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 3,
                  justifyContent: "center",
                  "@media (max-width: 768px)": {
                    flexDirection: "column",
                    gap: 2,
                  },
                }}
              >
                {steps.map((step, index) => {
                  const isCurrentStep = step.id === 1;
                  return (
                    <React.Fragment key={step.id}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          backgroundColor: isCurrentStep
                            ? "#EF5658"
                            : "#FAFAFA",
                          borderRadius: "40px",
                          px: 2,
                          width: "287px",
                          height: "58px",
                          "@media (max-width: 768px)": {
                            width: "329px",
                          },
                        }}
                      >
                        {/* Icon with circle background */}
                        <Box
                          sx={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            backgroundColor: isCurrentStep
                              ? "#F35D5F"
                              : "#F5F5F5",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 2,
                            flexShrink: 0,
                          }}
                        >
                          {isCurrentStep ? (
                            <CheckOutlinedIcon
                              sx={{
                                color: "#FFFFFF",
                                fontSize: "24px",
                              }}
                            />
                          ) : (
                            <CheckCircleOutlinedIcon
                              sx={{
                                color: "#666",
                                fontSize: "24px",
                              }}
                            />
                          )}
                        </Box>

                        {/* Text */}
                        <Box sx={{ flex: 1, alignItems: "center" }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: isCurrentStep ? "#FFFFFF" : "#000",
                              lineHeight: 1.5,
                            }}
                          >
                            {step.title}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: isCurrentStep ? "#FFFFFF" : "#666",
                              lineHeight: 1.8,
                              "@media (min-width: 768px) and (max-width: 1160px)": {
                                display: "none",
                              },
                            }}
                          >
                            {step.description}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Horizontal Divider between steps */}
                      {index < steps.length - 1 && (
                        <Box
                          sx={{
                            width: "40px",
                            height: "1px",
                            backgroundColor: "rgba(0, 0, 0, 0.12)",
                            "@media (max-width: 768px)": {
                              display: "none", // Hide dividers on mobile
                            },
                          }}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </Box>
            </Box>

            {/* Main Content Area*/}
            <Box
              sx={{
                backgroundColor: "#F0F0F0",
                p: 3,
                "@media (max-width: 768px)": {
                  p: 2,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  "@media (max-width: 1439px)": {
                    flexDirection: "column",
                  },
                }}
              >
                {/* Left Column - Product Information and Shipping Details */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    flex: 1,
                  }}
                >
                  {/* Product Information Card */}
                  <Box
                    sx={{
                      backgroundColor: "#FFFFFF",
                      borderRadius: "8px",
                      p: 2,
                      width: "778px",
                      height: "249px",
                      "@media (max-width: 856px)": {
                        width: "100%",
                        height: "auto",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "20px",
                        fontWeight: 600,
                        color: "#000",
                      }}
                    >
                      Product Information
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#666",
                        mb: 1,
                      }}
                    >
                      Details of the item you sold
                    </Typography>

                    <Divider sx={{ mb: 1.5 }} />

                    <Box
                      sx={{
                        display: "flex",
                        gap: 3,
                        "@media (max-width: 768px)": {
                          flexDirection: "column",
                        },
                      }}
                    >
                      <Box sx={{ justifyContent: "center", display: "flex" }}>
                        <Box
                          component="img"
                          src={orderData.product.image}
                          alt={orderData.product.name}
                          sx={{
                            width: "150px",
                            height: "150px",
                            borderRadius: "8px",
                            objectFit: "cover",
                            border: "1px solid #E0E0E0",
                          }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          component={Link}
                          to={`/toy/${orderData.product.toyUuid}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            fontSize: "20px",
                            fontWeight: 600,
                            color: "#000",
                            mb: 1,
                            textDecoration: "none",
                            cursor: "pointer",
                            "&:hover": {
                              color: "#EF5658",
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {orderData.product.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#666",
                            mb: 1,
                          }}
                        >
                          Condition:{" "}
                          <span style={{ color: "#333" }}>
                            {orderData.product.condition}
                          </span>
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#666",
                            mb: 1,
                          }}
                        >
                          Order ID:{" "}
                          <span style={{ color: "#333" }}>
                            #{orderData.orderId}
                          </span>
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#666",
                            mb: 1,
                          }}
                        >
                          Purchased on:{" "}
                          <span style={{ color: "#333" }}>
                            {orderData.product.purchaseDate}
                          </span>
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#666",
                          }}
                        >
                          Buyer:{" "}
                          <Link
                            to={`/user/${orderData.shipping.to.userUuid}/profile`}
                            style={{
                              color: "#EF5658",
                              textDecoration: "none",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.textDecoration = "underline";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.textDecoration = "none";
                            }}
                          >
                            {orderData.product.buyer}
                          </Link>
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Shipping Details Card */}
                  <Box
                    sx={{
                      backgroundColor: "#FFFFFF",
                      borderRadius: "8px",
                      p: 2,
                      width: "778px",
                      height: "259px",
                      "@media (max-width: 856px)": {
                        width: "100%",
                        height: "auto",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "20px",
                        fontWeight: 600,
                        color: "#000",
                      }}
                    >
                      Shipping Details
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#666",
                        mb: 1,
                      }}
                    >
                      Review pickup and delivery addresses
                    </Typography>

                    <Divider sx={{ mb: 1.5 }} />

                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        "@media (max-width: 768px)": {
                          flexDirection: "column",
                        },
                      }}
                    >
                      {/* Shipping From Box */}
                      <Box
                        sx={{
                          backgroundColor: "#FAFAFA",
                          borderRadius: "8px",
                          px: 3,
                          py: 1,
                          flex: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#000",
                          }}
                        >
                          Shipping From
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#666",
                            mb: 2,
                          }}
                        >
                          Pickup address for this shipment
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#000",
                              }}
                            >
                              {orderData.shipping.from.name}
                            </Typography>
                            <Typography
                              sx={{ fontSize: "14px", color: "#666" }}
                            >
                              {orderData.shipping.from.address}
                            </Typography>
                            <Typography
                              sx={{ fontSize: "14px", color: "#666" }}
                            >
                              {orderData.shipping.from.city}
                            </Typography>
                            <Typography
                              sx={{ fontSize: "14px", color: "#666" }}
                            >
                              {orderData.shipping.from.country}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* Shipping To Box */}
                      <Box
                        sx={{
                          backgroundColor: "#FAFAFA",
                          borderRadius: "8px",
                          px: 3,
                          py: 1,
                          flex: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#000",
                          }}
                        >
                          Shipping To
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#666",
                            mb: 2,
                          }}
                        >
                          Delivery address provided by the buyer
                        </Typography>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#000",
                            }}
                          >
                            {orderData.shipping.to.name}
                          </Typography>
                          <Typography sx={{ fontSize: "14px", color: "#666" }}>
                            {orderData.shipping.to.address}
                          </Typography>
                          <Typography sx={{ fontSize: "14px", color: "#666" }}>
                            {orderData.shipping.to.city}
                          </Typography>
                          <Typography sx={{ fontSize: "14px", color: "#666" }}>
                            {orderData.shipping.to.country}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Right Column - Cost Summary */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "390px",
                    "@media (max-width: 768px)": {
                      maxWidth: "390px",
                      width: "100%",
                    },
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: "#FFFFFF",
                      borderRadius: "8px",
                      p: 2,
                      height: "fit-content",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "20px",
                        fontWeight: 600,
                        color: "#000",
                      }}
                    >
                      Cost Summary
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#666",
                        mb: 1,
                      }}
                    >
                      Breakdown of shipping charges
                    </Typography>

                    <Divider sx={{ mb: 1.5 }} />

                    <Box sx={{ mb: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            lineHeight: 1.2,
                            color: "#000",
                          }}
                        >
                          Price
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#000",
                          }}
                        >
                          ${orderData.pricing.price.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              lineHeight: 1.2,
                              color: "#000",
                            }}
                          >
                            Platform Fee
                          </Typography>
                          <Typography sx={{ fontSize: "12px", color: "#666" }}>
                            Covers label generation and handling
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#000",
                          }}
                        >
                          ${orderData.pricing.platformFee.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          pt: 2,
                          borderTop: "1px solid #E0E0E0",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#000",
                          }}
                        >
                          Total
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#000",
                          }}
                        >
                          ${orderData.pricing.total.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Proceed Button */}
                  <Box sx={{ pt: 2 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<LocalShippingIcon />}
                      onClick={handleCreateLabel}
                      sx={{
                        backgroundColor: "#EF5658",
                        color: "#FFFFFF",
                        fontWeight: 600,
                        fontSize: "15px",
                        py: 1.5,
                        borderRadius: "8px",
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: "#D32F2F",
                        },
                      }}
                    >
                      Proceed To Create Shipping Label
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}
